/** Contact Us data-access layer — the category list and the submission mutation. */

import {
  ContactUsCategoriesDocument,
  type ContactUsCategoriesQuery,
  SubmitContactUsRequestDocument,
  type SubmitContactUsRequestFailureCode,
  type SubmitContactUsRequestInput,
} from '@mdvip/shared/graphql/generated/graphql';
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import type { ContactUsCategory } from '@mdvip/shared/lib/support/contactUs.types';

/**
 * Exported so `useSubmitContactUsRequest` and any future caller invalidate against the same key
 * instead of re-declaring it.
 */
export const contactUsCategoriesKey = ['support', 'contactUsCategories'] as const;

/** Thrown when `submitContactUsRequest` returns its Failure variant rather than a transport error. */
export class SubmitContactUsRequestError extends Error {
  constructor(public readonly code: SubmitContactUsRequestFailureCode) {
    super(code);
    this.name = 'SubmitContactUsRequestError';
  }
}

export function mapToContactUsCategories(data: ContactUsCategoriesQuery): ContactUsCategory[] {
  return data.contactUsCategories.map((category) => ({
    key: category.key,
    label: category.label,
    successMiddleLine: category.successMiddleLine,
    cancellationWarning: category.cancellationWarning ?? null,
    cancellationSuccessLine: category.cancellationSuccessLine ?? null,
  }));
}

export async function requestContactUsCategories(signal?: AbortSignal): Promise<ContactUsCategory[]> {
  const data = await gqlRequest(ContactUsCategoriesDocument, undefined, signal);
  return mapToContactUsCategories(data);
}

/**
 * Submits the request. Fire-and-forget: the mutation returns only the echoed category key, so
 * there is nothing to hand back to the caller on success.
 */
export async function requestSubmitContactUsRequest(input: SubmitContactUsRequestInput): Promise<void> {
  const data = await gqlRequest(SubmitContactUsRequestDocument, { input });
  const result = data.submitContactUsRequest;
  if (result.__typename === 'SubmitContactUsRequestFailure') {
    throw new SubmitContactUsRequestError(result.code);
  }
}
